import { getTextFilterProps } from './utils';
import { useEffect, useRef, useState } from 'react';
import {
  FieldType,
  OutputLayoutField,
  ProgramConfigCategory,
} from '@ssar-app/domain';
import { Text } from '@chakra-ui/react';
import { Table } from 'antd';

interface StandardLayoutProps {
  outputFields: OutputLayoutField[];
}

export const StandardOutputLayout = (props: StandardLayoutProps) => {
  const { outputFields } = props;
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [filteredFields, setFilteredFields] =
    useState<OutputLayoutField[]>(outputFields);

  const columns = [
    {
      title: '',
      align: 'center' as const,
      dataIndex: ['meta', 'index'],
      width: '50px',
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      width: '125px',
      filters: [
        { text: 'Experian', value: 'Experian' },
        { text: 'Client', value: 'Client' },
        { text: 'User', value: 'User' },
      ],
      onFilter: (value: string, record: OutputLayoutField) =>
        record.source && record.source.indexOf(value as string) === 0,
    },
    {
      title: 'Field Type',
      dataIndex: 'category',
      key: 'category',
      width: '150px',
      render: (value: string) => {
        return value ? (
          value === ProgramConfigCategory.OutputRequired ? (
            <Text color='purple.600'>Required</Text>
          ) : value === ProgramConfigCategory.Custom ||
            value === ProgramConfigCategory.ClientInput ? (
            <Text>Optional</Text>
          ) : (
            <Text>Selected</Text>
          )
        ) : (
          ''
        );
      },
      filters: Object.entries(FieldType).map(([text, value]) => ({
        text: text,
        value: value,
      })),
      onFilter: (value: string, record: OutputLayoutField) =>
        value === FieldType.Optional
          ? record.category === ProgramConfigCategory.Custom ||
            record.category === ProgramConfigCategory.ClientInput
          : value === FieldType.Required
          ? record.category === ProgramConfigCategory.OutputRequired
          : record.category !== ProgramConfigCategory.OutputRequired &&
            record.category !== ProgramConfigCategory.Custom &&
            record.category !== ProgramConfigCategory.ClientInput,
    },
    {
      title: 'Field Name',
      dataIndex: 'name',
      key: 'name',
      width: '275px',
      onFilter: (value: string, record: OutputLayoutField) =>
        record.name && record.name.includes(value.toUpperCase()),
      ...getTextFilterProps('Field Name', searchInputRef),
    },
    {
      title: 'Header Name',
      dataIndex: 'headerName',
      key: 'headerName',
      width: '275px',
      onFilter: (value: string, record: OutputLayoutField) =>
        record.headerName && record.headerName.includes(value.toUpperCase()),
      ...getTextFilterProps('Header Name', searchInputRef),
    },
    {
      title: 'Length',
      dataIndex: 'fieldLength',
      key: 'fieldLength',
      width: '100px',
      onFilter: (value: string, record: OutputLayoutField) =>
        record.fieldLength.toString() === value.toString(),
      ...getTextFilterProps('Length', searchInputRef),
    },
    {
      title: 'Start',
      dataIndex: ['meta', 'start'],
      key: 'start',
      width: '100px',
      onFilter: (value: string, record: OutputLayoutField) =>
        record.meta && record.meta.start.toString() === value.toString(),
      ...getTextFilterProps('Start', searchInputRef, 'number'),
    },
    {
      title: 'End',
      dataIndex: ['meta', 'end'],
      key: 'end',
      width: '100px',
      onFilter: (value: string, record: OutputLayoutField) =>
        record.meta && record.meta.end.toString() === value.toString(),
      ...getTextFilterProps('End', searchInputRef, 'number'),
    },
    {
      title: 'Constant Value',
      dataIndex: 'constantValue',
      key: 'constantValue',
      width: '150px',
      onFilter: (value: number, record: OutputLayoutField) =>
        record.constantValue &&
        record.constantValue.toString() === value.toString(),
      render: (value: string) => (value ? value : '--'),
      ...getTextFilterProps('Constant Value', searchInputRef),
    },
  ].filter((a) => a !== null);

  useEffect(() => {
    setFilteredFields(outputFields);
  }, [outputFields]);

  return (
    <Table
      size='small'
      className='custom-output-layout-table'
      dataSource={filteredFields}
      columns={columns}
      showSorterTooltip={false}
      bordered
      pagination={{
        hideOnSinglePage: false,
        defaultCurrent: 1,
        position: ['bottomLeft'],
        showTotal: (total, range) =>
          `Showing ${range[0]}-${range[1]} of ${total} items`,
        showSizeChanger: true,
        simple: false,
      }}
      rowKey={(row) => row.meta?.id || ''}
    />
  );
};
